/**
 * Social Responsibility Section
 * Extracted from FinancePage.jsx
 * Updated: Premium McKinsey-level charts
 */

import React from 'react';
import { Heart, Users, GraduationCap, Leaf, Home, Activity, TrendingUp, TrendingDown, Info } from 'lucide-react';
import {
  XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell, Legend,
  BarChart, Bar
} from 'recharts';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';

const SocialResponsibility = ({ year, language, flowMap }) => {
  // Data for 2025 and 2024
  const socialData = {
    2025: {
      totalInvestment: 4.2,
      totalBeneficiaries: 12500,
      programs: [
        { id: 'education', name: { pt: 'Educação', en: 'Education' }, value: 1.5, pct: 36, beneficiaries: 3200, icon: GraduationCap },
        { id: 'health', name: { pt: 'Saúde', en: 'Health' }, value: 1.2, pct: 29, beneficiaries: 4500, icon: Heart },
        { id: 'housing', name: { pt: 'Habitação', en: 'Housing' }, value: 0.8, pct: 19, beneficiaries: 850, icon: Home },
        { id: 'environment', name: { pt: 'Ambiente', en: 'Environment' }, value: 0.4, pct: 10, beneficiaries: 2800, icon: Leaf },
        { id: 'others', name: { pt: 'Outros', en: 'Others' }, value: 0.3, pct: 7, beneficiaries: 1150, icon: Users },
      ],
      kpis: {
        schoolsBuilt: 3,
        healthPostsSupported: 8,
        housesBuilt: 45,
        treesPlanted: 15000,
      }
    },
    2024: {
      totalInvestment: 3.8,
      totalBeneficiaries: 10900,
      programs: [
        { id: 'education', name: { pt: 'Educação', en: 'Education' }, value: 1.3, pct: 34, beneficiaries: 2800, icon: GraduationCap },
        { id: 'health', name: { pt: 'Saúde', en: 'Health' }, value: 1.1, pct: 29, beneficiaries: 3900, icon: Heart },
        { id: 'housing', name: { pt: 'Habitação', en: 'Housing' }, value: 0.7, pct: 18, beneficiaries: 720, icon: Home },
        { id: 'environment', name: { pt: 'Ambiente', en: 'Environment' }, value: 0.4, pct: 11, beneficiaries: 2400, icon: Leaf },
        { id: 'others', name: { pt: 'Outros', en: 'Others' }, value: 0.3, pct: 8, beneficiaries: 1080, icon: Users },
      ],
      kpis: {
        schoolsBuilt: 2,
        healthPostsSupported: 6,
        housesBuilt: 38,
        treesPlanted: 12000,
      }
    }
  };

  const currentData = socialData[year] || socialData[2025];
  const prevData = socialData[year - 1] || socialData[2024];

  // Calculate YoY change
  const totalChange = ((currentData.totalInvestment - prevData.totalInvestment) / prevData.totalInvestment * 100).toFixed(0);
  const beneficiariesChange = ((currentData.totalBeneficiaries - prevData.totalBeneficiaries) / prevData.totalBeneficiaries * 100).toFixed(0);

  // Premium pie chart colors - order matches programs: Educação, Saúde, Habitação, Ambiente, Outros
  const pieColors = ['#C5A059', '#059669', '#0A192F', '#3B82F6', '#64748B'];

  // Prepare pie data
  const pieData = currentData.programs.map((prog, idx) => ({
    name: prog.name[language],
    value: prog.value,
    percentage: prog.pct,
    fill: pieColors[idx],
    beneficiaries: prog.beneficiaries,
  }));

  // Custom Tooltip for premium look
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-[#0A192F] text-white rounded-lg px-4 py-3 shadow-xl border border-slate-700">
          <p className="font-semibold text-gold-400 mb-1">{data.name}</p>
          <p className="text-sm">{data.value}M USD</p>
          <p className="text-xs text-slate-400">{data.percentage}% do total</p>
          <p className="text-xs text-slate-400 mt-1">{data.beneficiaries.toLocaleString()} {language === 'pt' ? 'beneficiários' : 'beneficiaries'}</p>
        </div>
      );
    }
    return null;
  };

  // Custom Legend for pie chart (same style as OPEX)
  const CustomPieLegend = ({ payload }) => {
    return (
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-4 pt-4 border-t border-slate-100">
        {payload.map((entry, index) => {
          // Find the matching pieData item by name to get correct percentage
          const matchingData = pieData.find(d => d.name === entry.value);
          return (
            <div key={index} className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-xs text-slate-500">{entry.value}</span>
              <span className="text-xs font-semibold text-slate-700">{matchingData?.percentage}%</span>
            </div>
          );
        })}
      </div>
    );
  };

  // Custom label renderer for pie chart - positions labels outside for dark colors
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percentage, index, fill }) => {
    const RADIAN = Math.PI / 180;
    // Position labels outside the pie for better visibility
    const radius = outerRadius + 20;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    return (
      <text 
        x={x} 
        y={y} 
        fill="#334155"
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={11}
        fontWeight={600}
      >
        {`${percentage}%`}
      </text>
    );
  };

  // Prepare bar chart data for YoY comparison
  const barChartData = currentData.programs.map((prog, idx) => {
    const prevProg = prevData.programs.find(p => p.id === prog.id) || prog;
    const change = prog.value - prevProg.value;
    const changePercent = prevProg.value > 0 ? ((change / prevProg.value) * 100) : 0;
    return {
      name: prog.name[language],
      currentYear: prog.value,
      prevYear: prevProg.value,
      change: change,
      changePercent: parseFloat(changePercent.toFixed(1)),
    };
  });

  // Custom Tooltip for bar chart
  const CustomBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const currentVal = payload.find(p => p.dataKey === 'currentYear')?.value || 0;
      const prevVal = payload.find(p => p.dataKey === 'prevYear')?.value || 0;
      const change = currentVal - prevVal;
      const changePercent = prevVal > 0 ? ((change / prevVal) * 100).toFixed(0) : 0;
      const isIncrease = change >= 0;
      
      return (
        <div className="bg-[#0A192F] text-white rounded-lg px-4 py-3 shadow-xl border border-slate-700">
          <p className="font-semibold text-gold-400 mb-2">{label}</p>
          <div className="space-y-1">
            <p className="text-sm flex justify-between gap-4">
              <span className="text-slate-400">{year}:</span>
              <span className="font-semibold">{currentVal.toFixed(1)}M USD</span>
            </p>
            <p className="text-sm flex justify-between gap-4">
              <span className="text-slate-400">{year - 1}:</span>
              <span className="font-semibold">{prevVal.toFixed(1)}M USD</span>
            </p>
          </div>
          <div className="border-t border-slate-600 mt-2 pt-2">
            <p className={`text-xs ${isIncrease ? 'text-emerald-400' : 'text-red-400'}`}>
              Variação: {isIncrease ? '+' : ''}{change.toFixed(2)}M ({isIncrease ? '+' : ''}{changePercent}%)
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const socialInsights = {
    pt: [
      { title: 'INVESTIMENTO', description: `${currentData.totalInvestment}M USD investidos em ${year}, +${totalChange}% vs ano anterior.`, color: 'text-gold-400' },
      { title: 'IMPACTO', description: `${currentData.totalBeneficiaries.toLocaleString()} beneficiários directos nas comunidades locais.`, color: 'text-emerald-400' },
      { title: 'FOCO', description: 'Educação e Saúde representam 65% do investimento social total.', color: 'text-blue-400' },
    ],
    en: [
      { title: 'INVESTMENT', description: `${currentData.totalInvestment}M USD invested in ${year}, +${totalChange}% vs prior year.`, color: 'text-gold-400' },
      { title: 'IMPACT', description: `${currentData.totalBeneficiaries.toLocaleString()} direct beneficiaries in local communities.`, color: 'text-emerald-400' },
      { title: 'FOCUS', description: 'Education and Health represent 65% of total social investment.', color: 'text-blue-400' },
    ],
  };

  return (
    <div className="space-y-8" data-testid="social-responsibility-section">
      {/* Header */}
      <div>
        <h2 className="font-serif text-3xl font-bold text-primary">
          {language === 'pt' ? 'Responsabilidade Social' : 'Social Responsibility'}
        </h2>
        <p className="text-slate-500 mt-2">
          {language === 'pt' 
            ? 'Investimento social e impacto nas comunidades locais' 
            : 'Social investment and impact on local communities'}
        </p>
      </div>

      {/* Key Insights */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Insights'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-5">
          {socialInsights[language].map((insight, idx) => (
            <div key={idx} className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  idx === 0 ? 'bg-amber-500/30' : idx === 1 ? 'bg-emerald-500/30' : 'bg-blue-500/30'
                }`}>
                  {idx === 0 ? <Heart className="w-5 h-5 text-amber-400" /> : 
                   idx === 1 ? <Users className="w-5 h-5 text-emerald-400" /> : 
                   <Activity className="w-5 h-5 text-blue-400" />}
                </div>
                <div>
                  <h4 className={`font-semibold text-sm mb-1 ${insight.color}`}>{insight.title}</h4>
                  <p className="text-slate-400 text-xs leading-relaxed">{insight.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{language === 'pt' ? 'Investimento Total' : 'Total Investment'}</p>
          <p className="font-serif text-3xl font-semibold text-primary">{currentData.totalInvestment}<span className="text-base text-slate-400 ml-1">M USD</span></p>
          <div className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
            <TrendingUp className="w-3 h-3" />
            +{totalChange}% YoY
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{language === 'pt' ? 'Beneficiários' : 'Beneficiaries'}</p>
          <p className="font-serif text-3xl font-semibold text-primary">{(currentData.totalBeneficiaries / 1000).toFixed(1)}<span className="text-base text-slate-400 ml-1">k</span></p>
          <div className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
            <TrendingUp className="w-3 h-3" />
            +{beneficiariesChange}% YoY
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{language === 'pt' ? 'Escolas Construídas' : 'Schools Built'}</p>
          <p className="font-serif text-3xl font-semibold text-primary">{currentData.kpis.schoolsBuilt}</p>
          <p className="text-xs text-slate-500 mt-2">{language === 'pt' ? 'este ano' : 'this year'}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{language === 'pt' ? 'Postos de Saúde' : 'Health Posts'}</p>
          <p className="font-serif text-3xl font-semibold text-primary">{currentData.kpis.healthPostsSupported}</p>
          <p className="text-xs text-slate-500 mt-2">{language === 'pt' ? 'apoiados' : 'supported'}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{language === 'pt' ? 'Árvores Plantadas' : 'Trees Planted'}</p>
          <p className="font-serif text-3xl font-semibold text-primary">{(currentData.kpis.treesPlanted / 1000).toFixed(0)}<span className="text-base text-slate-400 ml-1">k</span></p>
          <div className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
            <TrendingUp className="w-3 h-3" />
            +25% YoY
          </div>
        </div>
      </div>

      {/* Executive Summary */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Responsabilidade social e impacto comunitário' : 'Social responsibility and community impact'}
        language={language}
        maxHeight="350px"
        icon={Info}
        dataTestId="social-responsibility-executive-summary"
      >
        <div className="prose prose-sm max-w-none text-slate-600 leading-relaxed">
          {language === 'pt' ? (
            <p>
              Em {year}, a ABC reforçou o seu compromisso com as comunidades locais, investindo <span className="font-semibold text-primary">{currentData.totalInvestment}M USD</span> em programas de responsabilidade social — um aumento de <span className="font-semibold text-emerald-600">+{totalChange}%</span> face ao ano anterior. Este investimento beneficiou directamente <span className="font-semibold text-primary">{currentData.totalBeneficiaries.toLocaleString()} pessoas</span> nas regiões onde operamos.
            </p>
          ) : (
            <p>
              In {year}, ABC reinforced its commitment to local communities, investing <span className="font-semibold text-primary">{currentData.totalInvestment}M USD</span> in social responsibility programs — an increase of <span className="font-semibold text-emerald-600">+{totalChange}%</span> compared to the previous year. This investment directly benefited <span className="font-semibold text-primary">{currentData.totalBeneficiaries.toLocaleString()} people</span> in the regions where we operate.
            </p>
          )}
          {language === 'pt' ? (
            <p className="mt-3">
              A <span className="font-semibold">Educação</span> e a <span className="font-semibold">Saúde</span> continuam a representar as principais áreas de intervenção, absorvendo conjuntamente <span className="font-semibold text-primary">65%</span> do investimento total. Destacam-se a construção de <span className="font-semibold">{currentData.kpis.schoolsBuilt} escolas</span>, o apoio a <span className="font-semibold">{currentData.kpis.healthPostsSupported} postos de saúde</span> e a edificação de <span className="font-semibold">{currentData.kpis.housesBuilt} habitações</span> para famílias locais. No âmbito ambiental, foram plantadas <span className="font-semibold">{(currentData.kpis.treesPlanted / 1000).toFixed(0)}k árvores</span>, reforçando o nosso compromisso com a sustentabilidade e a reabilitação das áreas mineiras.
            </p>
          ) : (
            <p className="mt-3">
              <span className="font-semibold">Education</span> and <span className="font-semibold">Health</span> remain the main areas of intervention, jointly absorbing <span className="font-semibold text-primary">65%</span> of total investment. Highlights include the construction of <span className="font-semibold">{currentData.kpis.schoolsBuilt} schools</span>, support for <span className="font-semibold">{currentData.kpis.healthPostsSupported} health posts</span>, and the building of <span className="font-semibold">{currentData.kpis.housesBuilt} homes</span> for local families. On the environmental front, <span className="font-semibold">{(currentData.kpis.treesPlanted / 1000).toFixed(0)}k trees</span> were planted, reinforcing our commitment to sustainability and mining area rehabilitation.
            </p>
          )}
        </div>
      </ExecutiveSummarySection>

      {/* Charts - Same layout as OPEX */}
      <div className="grid grid-cols-2 gap-6">
        {/* Premium Investment Breakdown Pie - Same style as OPEX Decomposition */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="mb-4">
            <h3 className="font-serif text-lg font-semibold text-primary">
              {language === 'pt' ? 'Investimento por Programa' : 'Investment by Program'}
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              {language === 'pt' ? 'Distribuição por área de intervenção' : 'Distribution by intervention area'}
            </p>
          </div>
          
          <ResponsiveContainer width="100%" height={300}>
            <RechartsPie>
              <Pie
                data={pieData}
                cx="50%"
                cy="45%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={2}
                dataKey="value"
                nameKey="name"
                label={renderCustomizedLabel}
                labelLine={false}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip content={<CustomPieTooltip />} />
              <Legend 
                content={<CustomPieLegend />}
                verticalAlign="bottom"
              />
            </RechartsPie>
          </ResponsiveContainer>
        </div>

        {/* Bar Chart - YoY Comparison by Program */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="mb-4">
            <h3 className="font-serif text-lg font-semibold text-primary">
              {language === 'pt' ? `Investimento por Programa | ${year} vs ${year - 1}` : `Investment by Program | ${year} vs ${year - 1}`}
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              {language === 'pt' ? 'Comparação face ao período homólogo' : 'Year-over-year comparison'}
            </p>
          </div>
          
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={barChartData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              barCategoryGap="20%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={true} vertical={false} />
              <XAxis 
                type="number" 
                tick={{ fontSize: 11, fill: '#64748B' }}
                axisLine={{ stroke: '#E2E8F0' }}
                tickFormatter={(value) => `${value}M`}
              />
              <YAxis 
                dataKey="name" 
                type="category" 
                tick={{ fontSize: 11, fill: '#334155', fontWeight: 500 }}
                axisLine={{ stroke: '#E2E8F0' }}
                width={80}
              />
              <Tooltip content={<CustomBarTooltip />} cursor={{ fill: 'rgba(203, 213, 225, 0.3)' }} />
              <Bar 
                dataKey="prevYear" 
                fill="#94A3B8" 
                name={`${year - 1}`}
                radius={[0, 4, 4, 0]}
                barSize={14}
              />
              <Bar 
                dataKey="currentYear" 
                fill="#0A192F" 
                name={`${year}`}
                radius={[0, 4, 4, 0]}
                barSize={14}
              />
            </BarChart>
          </ResponsiveContainer>
          
          {/* Legend */}
          <div className="flex justify-center gap-6 mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-4 h-3 rounded" style={{ backgroundColor: '#94A3B8' }}></div>
              <span className="text-xs text-slate-500">{year - 1}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-3 rounded" style={{ backgroundColor: '#0A192F' }}></div>
              <span className="text-xs text-slate-500">{year}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Programs Detail */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-serif text-lg font-semibold text-primary mb-4">
          {language === 'pt' ? 'Detalhe por Programa' : 'Program Detail'}
        </h3>
        <div className="grid grid-cols-5 gap-4">
          {currentData.programs.map((prog, idx) => {
            const prevProg = prevData.programs.find(p => p.id === prog.id) || prog;
            const change = ((prog.value - prevProg.value) / prevProg.value * 100).toFixed(0);
            const isPositive = prog.value >= prevProg.value;
            
            return (
              <div key={idx} className="text-center p-4 border border-slate-100 rounded-xl hover:shadow-md transition-shadow">
                <div className="w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${pieColors[idx]}20` }}>
                  <prog.icon className="w-6 h-6" style={{ color: pieColors[idx] }} />
                </div>
                <p className="text-sm font-medium text-primary">{prog.name[language]}</p>
                <p className="text-2xl font-bold text-primary mt-1">{prog.value}M</p>
                <div className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${isPositive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                  {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {isPositive ? '+' : ''}{change}%
                </div>
                <p className="text-xs text-slate-500 mt-2">{prog.beneficiaries.toLocaleString()} {language === 'pt' ? 'beneficiários' : 'beneficiaries'}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SocialResponsibility;
